import { computed, inject, Injectable, signal } from '@angular/core';
import { ProductApiService } from './product-api.service';
import {
  CartItem,
  CartItemOptions,
  CartItemState,
  CiAccessory,
  CiAmount,
  CiGuests,
  CiGroupItem,
  CiInputValue,
  CiPackageItem,
  CiPeriod,
  CiPoolUnit,
  CiLoadingState
} from '../model/cart-item';
import { AvailableSlotDto, PoolUnitDto, ProductInputValueResponse, ProductOrderRelationDto, PwAmountResponse } from '../model/product';

const GUEST_COUNT_LENGTH = 5;

function initialLoadingState(): CiLoadingState {
  return {
    product: false,
    amount: false,
    availability: false,
    schedule: false,
    timeItems: false,
    selectableDates: false,
    inputValues: false
  };
}

function emptyGuests(): CiGuests {
  return { counts: [0, 0, 0, 0, 0], total: 0, quantity: 1 };
}

function emptyPeriod(): CiPeriod {
  return { from: null, to: null, startTime: null, endTime: null, days: 0 };
}

function emptyState(): CartItemState {
  return {
    product: null,
    period: emptyPeriod(),
    guests: emptyGuests(),
    amount: null,
    availability: null,
    schedule: [],
    selectableDates: [],
    timeItems: [],
    accessories: [],
    groupItems: [],
    packageItems: [],
    poolUnits: [],
    inputValues: [],
    loading: initialLoadingState(),
    error: null
  };
}

@Injectable()
export class CartItemStateService {

  private readonly api = inject(ProductApiService);

  private readonly state = signal<CartItemState>(emptyState());
  private cid: string = '';
  private options: CartItemOptions = {};

  private requestIds: Record<keyof CiLoadingState, number> = {
    product: 0,
    amount: 0,
    availability: 0,
    schedule: 0,
    timeItems: 0,
    selectableDates: 0,
    inputValues: 0
  };

  readonly value = computed(() => this.state());

  readonly amount = computed(() => this.state().amount);
  readonly product = computed(() => this.state().product);
  readonly period = computed(() => this.state().period);
  readonly guests = computed(() => this.state().guests);
  readonly availability = computed(() => this.state().availability);
  readonly schedule = computed(() => this.state().schedule);
  readonly selectableDates = computed(() => this.state().selectableDates);
  readonly timeItems = computed(() => this.state().timeItems);
  readonly accessories = computed(() => this.state().accessories);
  readonly groupItems = computed(() => this.state().groupItems);
  readonly packageItems = computed(() => this.state().packageItems);
  readonly poolUnits = computed(() => this.state().poolUnits);
  readonly inputValues = computed(() => this.state().inputValues);
  readonly loading = computed(() => this.state().loading);
  readonly error = computed(() => this.state().error);

  readonly mandatoryAccessories = computed(() =>
    this.state().accessories.filter(a => a.mandatory || a.bookAlways));

  readonly optionalAccessories = computed(() =>
    this.state().accessories.filter(a => !(a.mandatory || a.bookAlways)));

  readonly isOrderable = computed(() => {
    const s = this.state();
    if (!s.availability) return false;
    return s.availability.orderable;
  });

  readonly totalAmount = computed(() => {
    const s = this.state();
    const amount = s.amount?.totalAmount ?? s.amount?.amount ?? 0;
    let total = amount;
    for (const a of s.accessories) {
      const isSelected = a.selected || (a.unlimited && a.quantity > 0);
      if (isSelected) {
        const qty = a.unlimited ? a.quantity : Math.max(1, a.quantity);
        total += (a.amount ?? 0) * qty;
      }
    }
    for (const g of s.groupItems) {
      total += (g.amount ?? 0) * Math.max(0, g.quantity);
    }
    for (const p of s.packageItems) {
      total += (p.amount ?? 0) * Math.max(1, p.quantity);
    }
    const selectedPool = s.poolUnits.find(p => p.selected);
    if (selectedPool && selectedPool.amount != null) {
      total += selectedPool.amount;
    }
    return total;
  });

  readonly canSubmit = computed(() => {
    const s = this.state();
    if (!s.product || !s.amount) return false;
    if (!this.isOrderable()) return false;
    for (const a of this.mandatoryAccessories()) {
      if (!a.available) return false;
    }
    const selectedPool = s.poolUnits.find(p => p.selected);
    if (s.poolUnits.length > 0 && !selectedPool) return false;
    return true;
  });

  async init(productId: number, cid: string, options: CartItemOptions = {}): Promise<void> {
    this.cid = cid;
    this.options = options;
    this.state.set(emptyState());

    this.patchLoading({ product: true });
    const productReq = ++this.requestIds.product;
    try {
      const product = await this.api.getProduct(cid, productId);
      if (productReq !== this.requestIds.product) return;
      this.patch({ product });
      const guests = this.resolveDefaultGuests(product);
      const initialGuests = options.initialGuests ?? guests;
      this.patch({ guests: initialGuests });

      // For PERIOD products, set period from product.periods[0] instead of
      // calling findFirstPossiblePeriod (which is for DATE/range products).
      if (product.timeConfig === 'PERIOD' && product.periods?.length) {
        const pp = product.periods.find(p => p.start != null && p.end != null) ?? product.periods[0];
        if (pp.start != null && pp.end != null) {
          const from = this.epochToIso(pp.start);
          const to = this.epochToIso(pp.end);
          this.patch({
            period: { ...this.state().period, from, to, days: this.daysBetween(from, to) }
          });
        }
      } else {
        await this.loadFirstPossiblePeriod(productId, initialGuests);
      }
      await this.refreshOrderData(productId);
    } catch (e: any) {
      if (productReq !== this.requestIds.product) return;
      this.patch({ error: e?.message || 'Failed to load product' });
    } finally {
      this.patchLoading({ product: false });
    }
  }

  async findFirstValidEndDate(from: string): Promise<string | null> {
    const product = this.state().product;
    if (!product) return null;
    const guests = this.guestsToString(this.state().guests);
    return this.api.getFirstValidEndDate(this.cid, product.id, from, 1, guests);
  }

  async isPeriodOrderable(from: string, to: string): Promise<boolean> {
    const product = this.state().product;
    if (!product) return false;
    const guests = this.guestsToString(this.state().guests);
    try {
      const res = await this.api.getAvailability(this.cid, product.id, {
        from, to, quantity: 1, guests
      });
      return !!res?.orderable;
    } catch {
      return false;
    }
  }

  async updatePeriod(from: string | null, to: string | null): Promise<void> {
    if (!from || !to) return;
    const period: CiPeriod = {
      ...this.state().period,
      from,
      to,
      days: this.daysBetween(from, to)
    };
    this.patch({ period });
    const productId = this.state().product?.id;
    if (productId) {
      await this.refreshOrderData(productId);
    }
  }

  async updateTime(startTime: string | null, endTime: string | null): Promise<void> {
    const period: CiPeriod = {
      ...this.state().period,
      startTime,
      endTime
    };
    this.patch({ period });
    const productId = this.state().product?.id;
    if (productId) {
      await this.loadAmount(productId);
    }
  }

  async updateGuests(counts: number[]): Promise<void> {
    const normalized = this.normalizeCounts(counts);
    const guests: CiGuests = {
      counts: normalized,
      total: normalized.reduce((a, b) => a + b, 0),
      quantity: this.state().guests.quantity
    };
    this.patch({ guests });
    const productId = this.state().product?.id;
    if (productId) {
      await this.refreshOrderData(productId);
    }
  }

  async updateQuantity(quantity: number): Promise<void> {
    const guests: CiGuests = {
      ...this.state().guests,
      quantity: Math.max(1, quantity)
    };
    this.patch({ guests });
    const productId = this.state().product?.id;
    if (productId) {
      await this.refreshOrderData(productId);
    }
  }

  toggleAccessory(accessoryId: number, selected: boolean): void {
    const accessories = this.state().accessories.map(a =>
      a.id === accessoryId ? { ...a, selected } : a
    );
    this.patch({ accessories });
    if (selected) {
      this.loadAccessoryAmount(accessoryId);
    }
  }

  async updateAccessoryQuantity(accessoryId: number, quantity: number): Promise<void> {
    const qty = Math.max(0, quantity);
    const accessories = this.state().accessories.map(a => {
      if (a.id !== accessoryId) return a;
      const updated = { ...a, quantity: qty };
      // For unlimited accessories, selected = quantity > 0
      if (a.unlimited) {
        updated.selected = qty > 0;
      }
      return updated;
    });
    this.patch({ accessories });
    const accessory = accessories.find(a => a.id === accessoryId);
    if (accessory) {
      await this.loadAccessoryAmount(accessory.id);
    }
  }

  updateAccessoryInputValue(accessoryId: number, inputValue: CiInputValue): void {
    const accessories = this.state().accessories.map(a =>
      a.id === accessoryId
        ? {
            ...a,
            inputValues: a.inputValues.map(iv =>
              iv.input.id === inputValue.input.id && iv.index === inputValue.index && iv.guestIndex === inputValue.guestIndex
                ? { ...iv, value: inputValue.value }
                : iv
            )
          }
        : a
    );
    this.patch({ accessories });
  }

  async loadAccessoryTimeSlots(accessoryId: number): Promise<void> {
    const accessory = this.state().accessories.find(a => a.id === accessoryId);
    const productId = accessory?.relatedProductId;
    if (!productId) return;
    const { period, guests } = this.state();
    if (!period.from || !period.to) return;
    try {
      const slots = await this.api.getAvailableSlots(this.cid, productId, {
        from: period.from,
        to: period.to,
        quantity: guests.quantity,
        guests: this.guestsToString(guests),
        maxSlots: 50
      });
      const accessories = this.state().accessories.map(a =>
        a.id === accessoryId ? { ...a, timeSlots: slots } : a
      );
      this.patch({ accessories });
    } catch (e: any) {
      // Keep existing slots (likely empty).
    }
  }

  async selectAccessoryTime(accessoryId: number, slot: AvailableSlotDto | null): Promise<void> {
    const accessories = this.state().accessories.map(a =>
      a.id === accessoryId
        ? {
            ...a,
            selectedTime: slot
              ? {
                  from: this.intDateToIso(slot.from),
                  to: this.intDateToIso(slot.to),
                  startTime: this.minutesToTime(slot.startTime),
                  endTime: this.minutesToTime(slot.endTime),
                  days: this.daysBetween(this.intDateToIso(slot.from), this.intDateToIso(slot.to))
                }
              : null
          }
        : a
    );
    this.patch({ accessories });
    await this.loadAccessoryAmount(accessoryId);
  }

  toggleGroupItem(groupItemId: number, quantity: number): void {
    const groupItems = this.state().groupItems.map(g =>
      g.id === groupItemId ? { ...g, quantity: Math.max(0, quantity) } : g
    );
    this.patch({ groupItems });
  }

  updatePackageItemQuantity(packageItemId: number, quantity: number): void {
    const packageItems = this.state().packageItems.map(p =>
      p.id === packageItemId ? { ...p, quantity: Math.max(0, quantity) } : p
    );
    this.patch({ packageItems });
  }

  selectPoolUnit(unitId: number | null): void {
    const poolUnits = this.state().poolUnits.map(p => ({
      ...p,
      selected: unitId != null && p.unitId === unitId
    }));
    this.patch({ poolUnits });
  }

  updateInputValue(inputValue: CiInputValue): void {
    const inputValues = this.state().inputValues.map(iv =>
      iv.input.id === inputValue.input.id && iv.index === inputValue.index && iv.guestIndex === inputValue.guestIndex
        ? { ...iv, value: inputValue.value }
        : iv
    );
    this.patch({ inputValues });
  }

  buildCartItem(): CartItem {
    const s = this.state();
    if (!s.product || !s.amount) {
      throw new Error('Cart item is not ready');
    }
    return {
      productId: s.product.id,
      product: s.product,
      period: s.period,
      guests: s.guests,
      amount: s.amount,
      accessories: s.accessories.filter(a => a.selected || a.mandatory || a.bookAlways || (a.unlimited && a.quantity > 0)),
      groupItems: s.groupItems.filter(g => g.quantity > 0),
      packageItems: s.packageItems.filter(p => p.quantity > 0),
      poolUnit: s.poolUnits.find(p => p.selected) ?? null,
      inputValues: s.inputValues
    };
  }

  private async loadFirstPossiblePeriod(productId: number, guests: CiGuests): Promise<void> {
    this.patchLoading({ selectableDates: true, timeItems: true });
    const req = ++this.requestIds.selectableDates;
    try {
      const period = await this.api.getFirstPossiblePeriod(this.cid, productId, {
        quantity: guests.quantity,
        guests: this.guestsToString(guests)
      });
      if (req !== this.requestIds.selectableDates) return;
      if (period.from && period.to) {
        this.patch({
          period: {
            from: this.intDateToIso(period.from),
            to: this.intDateToIso(period.to),
            startTime: period.startTime ? this.minutesToTime(period.startTime) : null,
            endTime: period.endTime ? this.minutesToTime(period.endTime) : null,
            days: this.daysBetween(this.intDateToIso(period.from), this.intDateToIso(period.to))
          }
        });
      }
    } catch (e: any) {
      if (req !== this.requestIds.selectableDates) return;
      this.patch({ error: e?.message || 'Failed to load first possible period' });
    } finally {
      this.patchLoading({ selectableDates: false, timeItems: false });
    }
  }

  private async refreshOrderData(productId: number): Promise<void> {
    const { period, guests } = this.state();
    if (!period.from || !period.to) return;

    const params = {
      from: period.from,
      to: period.to,
      quantity: guests.quantity,
      guests: this.guestsToString(guests)
    };

    await Promise.all([
      this.loadAmount(productId),
      this.loadAvailability(productId),
      this.loadSchedule(productId),
      this.loadTimeItems(productId),
      this.loadSelectableDates(productId),
      this.loadAccessories(params),
      this.loadGroupItems(params),
      this.loadPackageItems(params),
      this.loadPoolUnits(params),
      this.loadInputValues(productId)
    ]);
  }

  private async loadAmount(productId: number): Promise<void> {
    const { period, guests } = this.state();
    if (!period.from || !period.to) return;
    this.patchLoading({ amount: true });
    const req = ++this.requestIds.amount;
    try {
      const res = await this.api.getAmount(this.cid, productId, {
        from: period.from,
        to: period.to,
        quantity: guests.quantity,
        guests: this.guestsToString(guests),
        startTime: period.startTime ? this.timeToMinutes(period.startTime) : undefined,
        endTime: period.endTime ? this.timeToMinutes(period.endTime) : undefined
      });
      if (req !== this.requestIds.amount) return;
      const amount = this.mapAmountResponse(res);
      this.patch({ amount });
    } catch (e: any) {
      if (req !== this.requestIds.amount) return;
      this.patch({ amount: null, error: e?.message || 'Failed to load amount' });
    } finally {
      this.patchLoading({ amount: false });
    }
  }

  private async loadAvailability(productId: number): Promise<void> {
    const { period, guests } = this.state();
    if (!period.from || !period.to) return;
    this.patchLoading({ availability: true });
    const req = ++this.requestIds.availability;
    try {
      const res = await this.api.getAvailability(this.cid, productId, {
        from: period.from,
        to: period.to,
        quantity: guests.quantity,
        guests: this.guestsToString(guests)
      });
      if (req !== this.requestIds.availability) return;
      this.patch({ availability: { ...res, errorMessage: res.errorMessage ?? null } });
    } catch (e: any) {
      if (req !== this.requestIds.availability) return;
      this.patch({ availability: null, error: e?.message || 'Failed to load availability' });
    } finally {
      this.patchLoading({ availability: false });
    }
  }

  private async loadSchedule(productId: number): Promise<void> {
    const { period } = this.state();
    if (!period.from || !period.to) return;
    this.patchLoading({ schedule: true });
    const req = ++this.requestIds.schedule;
    try {
      const res = await this.api.getSchedule(this.cid, productId, { from: period.from, to: period.to });
      if (req !== this.requestIds.schedule) return;
      this.patch({ schedule: res });
    } catch (e: any) {
      if (req !== this.requestIds.schedule) return;
      this.patch({ schedule: [] });
    } finally {
      this.patchLoading({ schedule: false });
    }
  }

  private async loadTimeItems(productId: number): Promise<void> {
    const { period } = this.state();
    if (!period.from || !period.to) return;
    this.patchLoading({ timeItems: true });
    const req = ++this.requestIds.timeItems;
    try {
      const res = await this.api.getTimeItems(this.cid, productId, { from: period.from, to: period.to });
      if (req !== this.requestIds.timeItems) return;
      this.patch({ timeItems: res });
    } catch (e: any) {
      if (req !== this.requestIds.timeItems) return;
      this.patch({ timeItems: [] });
    } finally {
      this.patchLoading({ timeItems: false });
    }
  }

  private async loadSelectableDates(productId: number): Promise<void> {
    this.patchLoading({ selectableDates: true });
    const req = ++this.requestIds.selectableDates;
    try {
      const { guests } = this.state();
      const res = await this.api.getSelectableDates(this.cid, productId, { quantity: guests.quantity, guests: this.guestsToString(guests) });
      if (req !== this.requestIds.selectableDates) return;
      this.patch({ selectableDates: res });
    } catch (e: any) {
      if (req !== this.requestIds.selectableDates) return;
      this.patch({ selectableDates: [] });
    } finally {
      this.patchLoading({ selectableDates: false });
    }
  }

  private async loadAccessories(params: { from: string; to: string; quantity: number; guests: string }): Promise<void> {
    const product = this.state().product;
    if (!product?.hasAccessories) return;
    this.patchLoading({ ...this.state().loading, inputValues: true });
    const req = ++this.requestIds.inputValues;
    try {
      const res = await this.api.getAccessories(this.cid, product.id, params);
      if (req !== this.requestIds.inputValues) return;
      const accessories: CiAccessory[] = res.map((a: ProductOrderRelationDto) => ({
        ...a,
        selected: !!a.selectedByDefault || !!a.bookAlways || !!a.mandatory,
        quantity: a.unlimited ? 0 : 1,
        selectedTime: null,
        timeSlots: [],
        inputValues: (a.inputValues ?? []).map(iv => ({ ...iv, value: undefined }))
      }));
      this.patch({ accessories });
    } catch (e: any) {
      if (req !== this.requestIds.inputValues) return;
      this.patch({ accessories: [] });
    } finally {
      this.patchLoading({ ...this.state().loading, inputValues: false });
    }
  }

  private async loadGroupItems(params: { from: string; to: string; quantity: number; guests: string }): Promise<void> {
    const product = this.state().product;
    if (!product?.pcGroup) return;
    try {
      const res = await this.api.getGroupItems(this.cid, product.id, params);
      const groupItems: CiGroupItem[] = res.map((g: ProductOrderRelationDto) => ({ ...g, quantity: 0 }));
      this.patch({ groupItems });
    } catch (e: any) {
      this.patch({ groupItems: [] });
    }
  }

  private async loadPackageItems(params: { from: string; to: string; quantity: number; guests: string }): Promise<void> {
    const product = this.state().product;
    if (!product?.typePackage) return;
    try {
      const res = await this.api.getPackageItems(this.cid, product.id, params);
      const packageItems: CiPackageItem[] = res.map((p: ProductOrderRelationDto) => ({ ...p, quantity: 0 }));
      this.patch({ packageItems });
    } catch (e: any) {
      this.patch({ packageItems: [] });
    }
  }

  private async loadPoolUnits(params: { from: string; to: string; quantity: number; guests: string }): Promise<void> {
    const product = this.state().product;
    if (!product) return;
    try {
      const res = await this.api.getPoolUnits(this.cid, product.id, params);
      const poolUnits: CiPoolUnit[] = res.map((p: PoolUnitDto) => ({ ...p, selected: false }));
      this.patch({ poolUnits });
    } catch (e: any) {
      this.patch({ poolUnits: [] });
    }
  }

  private async loadInputValues(productId: number): Promise<void> {
    const guests = this.guestsToString(this.state().guests);
    try {
      const res = await this.api.getInputValues(this.cid, productId, guests);
      const inputValues: CiInputValue[] = res.map((iv: ProductInputValueResponse) => ({ ...iv, value: undefined }));
      this.patch({ inputValues });
    } catch (e: any) {
      this.patch({ inputValues: [] });
    }
  }

  private async loadAccessoryAmount(accessoryId: number): Promise<void> {
    const accessory = this.state().accessories.find(a => a.id === accessoryId);
    const productId = accessory?.relatedProductId;
    if (!productId) return;

    const { guests } = this.state();
    const period = accessory.selectedTime ?? this.state().period;
    if (!period.from || !period.to) return;

    const params: any = {
      from: period.from,
      to: period.to,
      quantity: 1,
      guests: this.guestsToString(guests)
    };
    if (period.startTime) params.startTime = this.timeToMinutes(period.startTime);
    if (period.endTime) params.endTime = this.timeToMinutes(period.endTime);

    // TODO: backenden kan i visse tilfeller kreve andre parametere for tilbehør
    // (f.eks. eget gjesteantall eller egne tidsparametre). Avklar og juster disse.

    try {
      const res = await this.api.getAmount(this.cid, productId, params);
      const amount = this.mapAmountResponse(res);
      const accessories = this.state().accessories.map(a =>
        a.id === accessoryId ? { ...a, amount: amount?.amount ?? a.amount } : a
      );
      this.patch({ accessories });
    } catch (e: any) {
      // keep existing amount
    }
  }

  private mapAmountResponse(res: PwAmountResponse): CiAmount {
    return {
      amount: this.toKroner(res.amountAsDouble, res.amount),
      retailAmount: this.toKroner(res.retailAmountAsDouble, res.retailAmount),
      unitAmount: this.toKroner(res.unitAmountDouble, res.unitAmount),
      totalAmount: this.toKroner(res.totalAmountDouble, res.totalAmount),
      days: res.days ?? 0,
      priceId: res.priceId ?? null,
      unitAmountGcMap: res.unitAmountGcMap ?? {}
    };
  }

  private toKroner(preferred: number | undefined, fallback: number | undefined): number {
    if (preferred !== undefined && preferred !== null) return preferred;
    if (fallback !== undefined && fallback !== null) return fallback / 100;
    return 0;
  }

  private resolveDefaultGuests(product: any): CiGuests {
    const counts = [0, 0, 0, 0, 0];
    const defaultGuests = product?.defaultGuests ?? 1;
    counts[0] = Math.max(0, defaultGuests);
    const total = counts.reduce((a, b) => a + b, 0);
    return { counts, total, quantity: 1 };
  }

  private normalizeCounts(counts: number[]): number[] {
    const result = [0, 0, 0, 0, 0];
    for (let i = 0; i < Math.min(counts.length, GUEST_COUNT_LENGTH); i++) {
      result[i] = Math.max(0, counts[i] || 0);
    }
    return result;
  }

  private guestsToString(guests: CiGuests): string {
    return guests.counts.join(',');
  }

  private daysBetween(from: string, to: string): number {
    const a = new Date(from);
    const b = new Date(to);
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.max(0, Math.round((b.getTime() - a.getTime()) / msPerDay));
  }

  private intDateToIso(value: number): string {
    const s = String(value);
    const year = s.substring(0, 4);
    const month = s.substring(4, 6);
    const day = s.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  private epochToIso(epoch: number): string {
    const d = new Date(epoch);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private timeToMinutes(time: string): number {
    const [h, m] = time.split(':');
    return (parseInt(h || '0', 10) * 60) + parseInt(m || '0', 10);
  }

  private minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  private patch(patch: Partial<CartItemState>): void {
    this.state.update(s => ({ ...s, ...patch }));
  }

  private patchLoading(loading: Partial<CiLoadingState>): void {
    this.state.update(s => ({ ...s, loading: { ...s.loading, ...loading } }));
  }
}
